import React from 'react';
import { Link } from 'react-router-dom';
import './IssPrimitives.scss';

type SectionTheme = 'light' | 'dark' | 'sand' | 'teal';
type SectionSpacing = 'default' | 'tight' | 'flush';
type HeadlineSize = 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
type HeadlineTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type SectionProps = {
  theme?: SectionTheme;
  spacing?: SectionSpacing;
  className?: string;
  children: React.ReactNode;
};

export const Section: React.FC<SectionProps> = ({
  theme = 'light',
  spacing = 'default',
  className = '',
  children,
}) => {
  const themeClass =
    theme === 'dark' ? 'iss-dark' : theme === 'sand' ? 'iss-sand' : theme === 'teal' ? 'iss-teal' : '';

  return (
    <section
      className={`iss-section iss-section--${spacing} ${themeClass} ${className}`.trim()}
      data-iss-theme={theme === 'light' ? undefined : theme}
    >
      <div className="iss-container">{children}</div>
    </section>
  );
};

type SimpleProps = {
  className?: string;
  children: React.ReactNode;
};

export const Eyebrow: React.FC<SimpleProps> = ({ className = '', children }) => (
  <p className={`iss-eyebrow ${className}`.trim()}>{children}</p>
);

type HeadlineProps = SimpleProps & {
  as?: HeadlineTag;
  size?: HeadlineSize;
};

export const Headline: React.FC<HeadlineProps> = ({
  as: Tag = 'h2',
  size = '3xl',
  className = '',
  children,
}) => (
  <Tag className={`iss-headline iss-headline--${size} ${className}`.trim()}>{children}</Tag>
);

export const Lead: React.FC<SimpleProps> = ({ className = '', children }) => (
  <p className={`iss-lead ${className}`.trim()}>{children}</p>
);

type CardProps = {
  title: string;
  text?: string;
  meta?: string;
  to?: string;
  href?: string;
  filled?: boolean;
  className?: string;
  children?: React.ReactNode;
};

function isExternalHref(href: string): boolean {
  return /^(https?:)?\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:');
}

export const Card: React.FC<CardProps> = ({
  title,
  text,
  meta,
  to,
  href,
  filled = false,
  className = '',
  children,
}) => {
  const classes = `iss-card ${filled ? 'iss-card--filled' : ''} ${className}`.trim();
  const body = (
    <>
      {meta ? <span className="iss-card__meta">{meta}</span> : null}
      <h3 className="iss-card__title">{title}</h3>
      {text ? <p className="iss-card__text">{text}</p> : null}
      {children}
      {(to || href) && <span className="iss-card__action">Open →</span>}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes}>
        {body}
      </Link>
    );
  }

  if (href) {
    const external = isExternalHref(href);
    return (
      <a
        href={href}
        className={classes}
        {...(external
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      >
        {body}
      </a>
    );
  }

  return <div className={classes}>{body}</div>;
};

type GridProps = SimpleProps;

export const Grid: React.FC<GridProps> = ({ className = '', children }) => (
  <div className={`iss-grid ${className}`.trim()}>{children}</div>
);

type ColProps = SimpleProps & {
  span?: 1 | 2 | 3 | 4;
  md?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  lg?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
};

export const Col: React.FC<ColProps> = ({
  span = 4,
  md,
  lg,
  className = '',
  children,
}) => {
  const mdClass = md ? `iss-col-md-${md}` : '';
  const lgClass = lg ? `iss-col-lg-${lg}` : '';
  return (
    <div className={`iss-col iss-col-${span} ${mdClass} ${lgClass} ${className}`.trim()}>
      {children}
    </div>
  );
};
